/* eslint-disable @typescript-eslint/naming-convention */
import axios from 'axios';
import { AxiosInstance, AxiosRequestConfig } from 'axios';
import urlJoin from 'proper-url-join';
import { Injectable } from '@angular/core';
import { StorageService } from './storage.service';

const DEFAULT_SERIAL_PORT = 0;
const timeout = 60000;
const subsequentBytesPattern = '11111';

const STANDARD_TAGS = [
  '5F2A', '82', '84', '95', '9A', '9C', '9F02', '9F03', '9F09', '9F10', '9F1A', '9F1E',
  '9F26', '9F27', '9F33', '9F34', '9F35', '9F36', '9F37', '9F41', '9F53', '5F34',
];

const defaultTagValues = {
  '9F03': '0169F03000000000000',
  // '9F53': '0069F5352',
};

const POS_SERVICE_CONFIG = {
  protocol: 'http',
  server: '127.0.0.1',
  port: '5000',
  rootPath: '/',
};

const API_METHODS = {
  setWorkingKey: { method: 'PUT', path: 'ewk' },
  getSerialPort: { method: 'GET', path: 'serial' },
  setSerialPort: { method: 'PUT', path: 'serial' },
  getPrinter: { method: 'GET', path: 'impresora' },
  print: { method: 'PUT', path: 'impresora' },
  readCard: { method: 'GET', path: 'tarjeta' },
  checkArpc: { method: 'PATCH', path: 'tarjeta/valida' },
  readPin: { method: 'GET', path: 'pinpad' },
  getDeviceSerial: { method: 'GET', path: 'numerodeserie' },
};

const DEFAULT_ERROR = {
  errorCode: '99',
  errorFullCode: '',
  errorName: 'posServiceAccess',
  errorMessage: 'Error accediendo al servicio de POS',
  errorDetail: '',
};

interface PosResponse {
  data: any;
  error: any;
}

class VoucherLine {
  dosColumnnas: boolean = false;
  dobleAlto: boolean = false;
  dobleAncho: boolean = false;
  invertir: boolean = false;
  negrita: boolean = false;
  alineacion: number = 0;
  texto: string = '';
  texto2: string = '';
  tamano: number = 1;
}

// async function wait(ms) { return new Promise((resolve) => { setTimeout(resolve, ms); }); }

@Injectable({ providedIn: 'root' })
export class PosInterfaceService {
  private axiosClient: AxiosInstance;
  portNumber: number = 0;
  terminalSerial: string = '';
  supportPrinting: boolean = true;
  constructor(
    private _storageService: StorageService,
  ) {
    const validateStatus = status => (status >= 200 && status < 600);
    const axiosClientParams = {
      baseURL: this.setBaseUrlApi(POS_SERVICE_CONFIG),
      timeout,
      headers: { 'Content-Type': 'application/json' },
      validateStatus,
    };
    this.axiosClient = axios.create(axiosClientParams);
    this.initPosService();
  }

  async initPosService() {
    await this.initSerialPort();
  }

  async initSerialPort() {
    const { data } = await this.getSerialPort();
    const servicePortNumber = data?.portNumber ?? DEFAULT_SERIAL_PORT;
    const savedPortNumber = +this._storageService?.getItem('terminalPorNumber') ?? DEFAULT_SERIAL_PORT;
    if (savedPortNumber !== 0 && savedPortNumber !== servicePortNumber) {
      await this.setSerialPort(savedPortNumber);
    } else {
      this._storageService.setItem('terminalPorNumber', servicePortNumber);
    }
  }

  canPrint() {
    return this.supportPrinting;
  }

  setRemoteSerial(remoteSerial) {
    this.terminalSerial = remoteSerial;
  }

  getRemoteSerial() {
    return this.terminalSerial;
  }

  setBaseUrlApi(serverEntryPoint) {
    let protocol = serverEntryPoint.protocol ?? window.location.protocol;
    protocol = protocol.endsWith(':') ? protocol.slice(0, -1) : protocol;
    let server = serverEntryPoint.server ?? window.location.hostname;
    server = server.endsWith('/') ? server.slice(0, -1) : server;
    let baseUrl = `${protocol}://${server}`;
    const port = serverEntryPoint.port ?? window.location.port;
    baseUrl += port ? `:${port}` : '';
    return urlJoin(baseUrl, serverEntryPoint.rootPath);
  }

  async callSrv(methodDefinition, data: any = null): Promise<PosResponse> {
    let methodResponse: PosResponse = null;
    const requestError = JSON.parse(JSON.stringify(DEFAULT_ERROR));
    const { method, path: url } = methodDefinition ?? {};
    let responseBody;
    try {
      let axiosRequest: AxiosRequestConfig;
      if (method === 'GET') {
        axiosRequest = { url, method, params: data };
      } else {
        axiosRequest = { url, method, data };
      }
      const axiosResponse = await this.axiosClient.request(axiosRequest);
      const { data: serviceAnswer, status, statusText } = axiosResponse ?? {};
      responseBody = serviceAnswer;
      if (status && +status !== 200) {
        requestError.errorDetail = responseBody?.detail ?? `Error no identificado ${statusText}`;
        methodResponse = { error: requestError, data: responseBody };
      } else {
        methodResponse = { error: null, data: responseBody };
      }
    } catch (err) {
      requestError.errorDetail = `Error accediendo al servicio ${url}: ${responseBody?.detail ?? err?.message}`;
      if (!err.response) {
        requestError.errorDetail += ' Sin respuesta';
      }
      methodResponse = { error: requestError, data: null };
    }
    return methodResponse;
  }

  reformatEmvTags(emvData) {
    const resultTags = {};
    let formatedEmv = '';
    let emvRemaining = emvData;
    while (emvRemaining.length > 0) {
      const tagHexaCode = emvRemaining.slice(0, 2);
      emvRemaining = emvRemaining.slice(2);
      let tagOnBinary = (parseInt(tagHexaCode, 16).toString(2)).padStart(8, '0');
      let fullTagHexaCode = tagHexaCode;
      let subsequentBytes = tagOnBinary.slice(-5) === subsequentBytesPattern;
      while (subsequentBytes && emvRemaining.length > 0) {
        const tagNextByte = emvRemaining.slice(0, 2);
        emvRemaining = emvRemaining.slice(2);
        tagOnBinary = (parseInt(tagNextByte, 16).toString(2)).padStart(8, '0');
        fullTagHexaCode += tagNextByte;
        subsequentBytes = tagOnBinary[0] === '1';
      }
      let tagLength = 0;
      let tagLengthHexa = emvRemaining.slice(0, 2);
      const tagLengthBytes = (parseInt(tagLengthHexa, 16).toString(2)).padStart(8, '0');
      if (tagLengthBytes[0] === '0') {
        tagLength = parseInt(tagLengthHexa, 16);
        emvRemaining = emvRemaining.slice(2);
      } else {
        const numLengthBytes = parseInt(tagLengthBytes.slice(-7), 2);
        tagLengthHexa = emvRemaining.slice(0, numLengthBytes);
        tagLength = parseInt(tagLengthHexa, 16);
        emvRemaining = emvRemaining.slice(numLengthBytes);
      }
      const tagContent = emvRemaining.slice(0, 2 * tagLength);
      emvRemaining = emvRemaining.slice(2 * tagLength);
      const formattedLength = (2 * tagLength + 4).toString().padStart(3, '0');
      const formatedTag = `${formattedLength}${fullTagHexaCode.padEnd(4, ' ')}${tagContent}`;
      if (STANDARD_TAGS.includes(fullTagHexaCode)) {
        resultTags[fullTagHexaCode] = formatedTag;
      }
    }
    STANDARD_TAGS.forEach((tag) => {
      const defaultTagValue = defaultTagValues[tag];
      formatedEmv += resultTags[tag] ?? defaultTagValue ?? '';
    });
    return formatedEmv.trim();
  }

  get defaultPortNumber() {
    return this.portNumber;
  }

  async getDeviceSerial() {
    return this.callSrv(API_METHODS.getDeviceSerial);
  }

  async readCard() {
    const { error, data } = await this.callSrv(API_METHODS.readCard);
    if (!error) {
      const cardTrack = data?.track2;
      const [cardNumber, cardTrackShort] = cardTrack?.split('=');
      const cardReadResult = { cardNumber, cardTrack, cardTrackShort, emv: this.reformatEmvTags(data?.tags) };
      return { error: null, data: cardReadResult };
    }
    return { error, data };
  }

  async readPin(capturePinText = null) {
    const { error, data } = await this.callSrv(API_METHODS.readPin, { mensaje: capturePinText });
    if (!data?.pinblock) {
      const requestError = JSON.parse(JSON.stringify(DEFAULT_ERROR));
      requestError.errorDetail = `No se leyo PIN valido`;
      return { error: requestError, data }
    }
    return { error, data: { pinBlock: data?.pinblock } };
  }

  async readCardAndPin(capturePinMessages, readCallback = null) {
    const { error: readCardError, data: cardInfo } = await this.readCard();
    if (readCardError) {
      return { error: readCardError, data: null };
    }
    readCallback && readCallback({ cardRead: true });
    const { error, data: pinBlockArray } = await this.readMultiplePin(capturePinMessages, readCallback);
    return { error, data: { cardInfo, pinBlockArray } };
  }

  async readMultiplePin(capturePinMessages, readCallback = null) {
    const pinBlockArray = [];
    for (let index = 0; index < capturePinMessages.length; index++) {
      const capturePinText = capturePinMessages[index];
      const { error, data } = await this.readPin(capturePinText);
      if (error) {
        return { error, data: pinBlockArray };
      }
      readCallback && readCallback({ pinRead: true, index });
      pinBlockArray.push(data);
    }
    return { error: null, data: pinBlockArray };
  }

  async getSerialPort() {
    return this.callSrv(API_METHODS.getSerialPort);
  }

  async setSerialPort(portNumber) {
    if (+portNumber !== 0) {
      this.portNumber = +portNumber;
      this._storageService.setItem('terminalPorNumber', portNumber);
      return this.callSrv(API_METHODS.setSerialPort, { portNumber });
    }
    return { error: { mensaje: 'Numero de puerto inválido' }, data: null };
  }

  async setWorkingKey(value) {
    return this.callSrv(API_METHODS.setWorkingKey, { value });
  }

  async printReceipt(dataLines, copy = false) {
    const voucher: any[] = [];
    const voucherLine: VoucherLine = new VoucherLine();
    if (!dataLines || !Array.isArray(dataLines)) {
      return [null, null];
    }
    copy && (voucher.push({ ...voucherLine, texto: 'COPIA', alineacion: 1, dobleAlto: true }));
    dataLines?.forEach(line => voucher.push({ ...voucherLine, ...line }));
    voucher.push({ ...voucherLine });
    voucher.push({ ...voucherLine, tamano: 0, texto: '*** No revele su clave a nadie ***', alineacion: 1, dobleAlto: true });
    return this.callSrv(API_METHODS.print, { voucher });
  }
}
